import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  Heading,
  Input,
  Text,
  useToast,
  VStack,
  HStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Textarea,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaLightbulb } from 'react-icons/fa';
import supabase from '../utils/supabaseUtils'; 
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const DevManagement = () => {
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium'
  });
  
  // Editing group state
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');

  const toast = useToast();
  const postItBg = useColorModeValue('yellow.100', 'yellow.700');
  const postItShadow = 'lg';

  useEffect(() => {
    fetchGroups();
    fetchTasks();
  }, []);

  const fetchGroups = async () => {
    const { data, error } = await supabase
      .from('task_groups')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      toast({
        title: 'Error fetching groups',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setGroups(data || []);
    }
  };

  const fetchTasks = async () => {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      toast({
        title: 'Error fetching tasks',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setTasks(data || []);
    }
  };

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;

    const { data, error } = await supabase
      .from('task_groups')
      .insert([
        {
          name: newGroupName,
          order_index: groups.length
        }
      ])
      .select()
      .single();

    if (error) {
      toast({
        title: 'Error adding group',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setGroups([...groups, data]);
      setNewGroupName('');
      setIsAddingGroup(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title.trim() || !selectedGroup) return;

    const { data, error } = await supabase
      .from('tasks')
      .insert([
        {
          title: newTask.title,
          description: newTask.description,
          priority: newTask.priority,
          group_id: selectedGroup,
          order_index: tasks.filter(t => t.group_id === selectedGroup).length
        }
      ])
      .select()
      .single();

    if (error) {
      toast({
        title: 'Error adding task',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setTasks([...tasks, data]);
      setNewTask({
        title: '',
        description: '',
        priority: 'medium'
      });
      setIsAddingTask(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    const { error } = await supabase
      .from('tasks')
      .update({ status: 'completed' })
      .eq('id', taskId);

    if (error) {
      toast({
        title: 'Error completing task',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } else {
      setTasks(tasks.map(task => 
        task.id === taskId ? { ...task, status: 'completed' } : task
      ));
    }
  };

  const getAISuggestions = async (taskId) => {
    // This is where we'll integrate with an AI service
    // For now, just show a placeholder
    toast({
      title: 'AI Suggestions',
      description: 'AI integration coming soon!',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // Helper: build task tree for nested rendering
  const buildTaskTree = (groupId, parentId = null) => {
    return tasks
      .filter(
        (t) => t.group_id === groupId && (t.parent_id || null) === (parentId || null)
      )
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  };

  // DnD handler
  const onDragEnd = async (result) => {
    const { source, destination, draggableId, type } = result;
    if (!destination) return;

    // -------------------- GROUP REORDER --------------------
    if (type === 'group') {
      if (source.index === destination.index) return;

      const newGroups = Array.from(groups);
      const [moved] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, moved);

      // Re-index and persist order
      try {
        for (let i = 0; i < newGroups.length; i++) {
          newGroups[i].order_index = i;
          await supabase
            .from('task_groups')
            .update({ order_index: i })
            .eq('id', newGroups[i].id);
        }
        setGroups(newGroups);
      } catch (err) {
        toast({
          title: 'Group reorder error',
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }

      return; // Done processing group type
    }

    // -------------------- TASK REORDER --------------------
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const draggedId = parseInt(draggableId.replace('task-', ''), 10);
    const srcParts = source.droppableId.split('-');
    const destParts = destination.droppableId.split('-');

    // Determine new grouping/parenting context
    let newGroupId = null;
    let newParentId = null;

    if (destParts[0] === 'group') {
      newGroupId = parseInt(destParts[1], 10);
    } else if (destParts[0] === 'task') {
      const parentTask = tasks.find((t) => t.id === parseInt(destParts[1], 10));
      if (parentTask) {
        newGroupId = parentTask.group_id;
        newParentId = parentTask.id;
      }
    }

    const updatedTasks = [...tasks];
    const draggedTaskIndex = updatedTasks.findIndex((t) => t.id === draggedId);
    const draggedTask = updatedTasks[draggedTaskIndex];

    if (!draggedTask) return;

    // Update local task meta first for immediate UI feedback
    draggedTask.group_id = newGroupId;
    draggedTask.parent_id = newParentId;

    // Re-order siblings within the new parent context
    const siblings = updatedTasks
      .filter(
        (t) =>
          t.id !== draggedId &&
          t.group_id === newGroupId &&
          (t.parent_id || null) === (newParentId || null)
      )
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

    siblings.splice(destination.index, 0, draggedTask);

    siblings.forEach((t, idx) => {
      t.order_index = idx;
    });

    // Merge back into state
    setTasks(updatedTasks);

    // Persist DB changes
    try {
      // Update parent/group for dragged task
      await supabase
        .from('tasks')
        .update({
          group_id: newGroupId,
          parent_id: newParentId,
          order_index: destination.index,
        })
        .eq('id', draggedId);

      // Batch update sibling order indices
      for (const t of siblings) {
        await supabase
          .from('tasks')
          .update({ order_index: t.order_index })
          .eq('id', t.id);
      }
    } catch (err) {
      toast({
        title: 'Drag-and-drop error',
        description: err.message || 'Failed to update task order',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleRenameGroup = async (groupId) => {
    if (!editingGroupName.trim()) {
      setEditingGroupId(null);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('task_groups')
        .update({ name: editingGroupName })
        .eq('id', groupId)
        .select()
        .single();

      if (error) throw error;

      setGroups(
        groups.map((g) => (g.id === groupId ? { ...g, name: data.name } : g))
      );
      setEditingGroupId(null);
    } catch (err) {
      toast({
        title: 'Error renaming group',
        description: err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Recursive renderer for tasks with nesting & DnD
  const renderTasks = (groupId, parentId = null, level = 0) => {
    const tree = buildTaskTree(groupId, parentId);
    return tree.map((task, index) => (
      <Draggable key={task.id} draggableId={`task-${task.id}`} index={index}>
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            bg={postItBg}
            p={4}
            borderRadius="md"
            boxShadow={postItShadow}
            transform="rotate(-1deg)"
            transition="all 0.2s"
            _hover={{ transform: 'rotate(0deg) scale(1.02)' }}
            ml={`${level * 20}px`}
          >
            <Flex justify="space-between" align="start">
              <Text
                textDecoration={task.status === 'completed' ? 'line-through' : 'none'}
                fontSize="md"
                fontWeight="bold"
              >
                {task.title}
              </Text>
              <HStack>
                <IconButton
                  icon={<FaLightbulb />}
                  size="sm"
                  variant="ghost"
                  onClick={() => getAISuggestions(task.id)}
                />
                <IconButton
                  icon={task.status === 'completed' ? <FaTimes /> : <FaCheck />}
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCompleteTask(task.id)}
                />
              </HStack>
            </Flex>
            {task.description && (
              <Text fontSize="sm" mt={2} color="gray.600">
                {task.description}
              </Text>
            )}

            {/* Nested Droppable for subtasks */}
            <Droppable droppableId={`task-${task.id}`} type="task">
              {(dropProvided) => (
                <VStack
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  spacing={4}
                  align="stretch"
                  mt={4}
                >
                  {renderTasks(groupId, task.id, level + 1)}
                  {dropProvided.placeholder}
                </VStack>
              )}
            </Droppable>
          </Box>
        )}
      </Draggable>
    ));
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justify="space-between" align="center" mb={8}>
        <Heading>Dev Management</Heading>
        <Button leftIcon={<FaPlus />} colorScheme="blue" onClick={() => setIsAddingGroup(true)}>
          Add Group
        </Button>
      </Flex>

      {/* DnD context */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="groups-root" type="group" direction="vertical">
          {(groupsProvided) => (
            <Grid
              ref={groupsProvided.innerRef}
              {...groupsProvided.droppableProps}
              templateColumns="repeat(auto-fill, minmax(300px, 1fr))"
              gap={6}
            >
              {groups
                .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                .map((group, index) => (
                  <Draggable
                    key={group.id}
                    draggableId={`group-${group.id}`}
                    index={index}
                  >
                    {(groupProv) => (
                      <Box
                        ref={groupProv.innerRef}
                        {...groupProv.draggableProps}
                        bg={useColorModeValue('gray.50', 'gray.700')}
                        p={4}
                        borderRadius="lg"
                        boxShadow="md"
                      >
                        {/* Header with drag handle */}
                        <Flex
                          justify="space-between"
                          align="center"
                          mb={4}
                          {...groupProv.dragHandleProps}
                        >
                          {editingGroupId === group.id ? (
                            <Input
                              size="sm"
                              value={editingGroupName}
                              onChange={(e) => setEditingGroupName(e.target.value)}
                            />
                          ) : (
                            <Heading size="md">{group.name}</Heading>
                          )}

                          <HStack spacing={1}>
                            {editingGroupId === group.id ? (
                              <>
                                <IconButton
                                  icon={<FaCheck />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRenameGroup(group.id)}
                                />
                                <IconButton
                                  icon={<FaTimes />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingGroupId(null)}
                                />
                              </>
                            ) : (
                              <>
                                <IconButton
                                  icon={<FaEdit />}
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingGroupId(group.id);
                                    setEditingGroupName(group.name);
                                  }}
                                />
                                <IconButton
                                  icon={<FaPlus />}
                                  size="sm"
                                  onClick={() => {
                                    setSelectedGroup(group.id);
                                    setIsAddingTask(true);
                                  }}
                                />
                              </>
                            )}
                          </HStack>
                        </Flex>

                        <Droppable droppableId={`group-${group.id}`} type="task">
                          {(provided) => (
                            <VStack
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              spacing={4}
                              align="stretch"
                            >
                              {renderTasks(group.id, null, 0)}
                              {provided.placeholder}
                            </VStack>
                          )}
                        </Droppable>
                      </Box>
                    )}
                  </Draggable>
                ))}
              {groupsProvided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add Group Modal */}
      <Modal isOpen={isAddingGroup} onClose={() => setIsAddingGroup(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Group</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Input
              placeholder="Group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <Button mt={4} colorScheme="blue" onClick={handleAddGroup}>
              Add Group
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Add Task Modal */}
      <Modal isOpen={isAddingTask} onClose={() => setIsAddingTask(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Task</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Input
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              />
              <Textarea
                placeholder="Task description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              />
              <Select
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </Select>
              <Button colorScheme="blue" onClick={handleAddTask}>
                Add Task
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default DevManagement; 